
import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { Logo } from "@/components/Logo";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { clearRedirectionFlags, getAbsoluteUrl } from "@/utils/domainUtils";
import { Eye, EyeOff, RefreshCw } from "lucide-react";

// LoginContent component that handles the actual login logic
const LoginContent = () => {
  console.log("Login: Component rendering");
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { isAuthenticated, isLoading, signIn } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  // Clear any redirection flags when login page loads
  useEffect(() => {
    clearRedirectionFlags();
    console.log("Login page: Cleared redirection flags");
    console.log("Login page loaded. Auth state:", { isAuthenticated, isLoading });
    console.log("Current location:", location);
  }, []);

  // Redirect logic with improved logging and error handling
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      try {
        const redirectPath = location.state?.from || "/dashboard";
        console.log(`Login: Redirecting authenticated user to ${redirectPath}`);

        setTimeout(() => {
          navigate(redirectPath, { replace: true });
        }, 0);
      } catch (error) {
        console.error("Login: Navigation error", error);
        setTimeout(() => {
          window.location.href = getAbsoluteUrl('/dashboard');
        }, 100);
      }
    }
  }, [isAuthenticated, isLoading, navigate, location.state]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage("");
    
    if (isSubmitting) return;
    if (!email.trim() || !password.trim()) {
      setErrorMessage("Please enter both email and password");
      return;
    }

    setIsSubmitting(true);
    
    try {
      console.log("Login: Attempting sign in with email:", email);
      const success = await signIn(email, password);
      
      if (success) {
        console.log("Login: Sign in successful, waiting for redirect");
        setRetryCount(0); // Reset retry count on success
        // Let the useEffect handle the redirection
      } else {
        setErrorMessage("Authentication failed. Please check your credentials.");
        setIsSubmitting(false);
      }
    } catch (error: any) {
      console.error("Login: Sign in failed", error);
      
      // Enhanced error handling with specific messages
      let userMessage = "Please check your credentials and try again";
      
      if (error.message?.includes('function') && error.message?.includes('does not exist')) {
        userMessage = "Authentication system not set up. Please contact your administrator.";
      } else if (error.message?.includes('Database connection failed')) {
        userMessage = "Cannot connect to the database. Please try again later or contact support.";
      } else if (error.message?.includes('network') || error.message?.includes('connection')) {
        userMessage = "Network error. Please check your connection and try again.";
      } else if (error.message?.includes('service') || error.message?.includes('unavailable')) {
        userMessage = "Authentication service is temporarily unavailable. Please try again in a moment.";
      } else if (error.message?.includes('expired') || error.message?.includes('session')) {
        userMessage = "Your session has expired. Please try logging in again.";
      } else if (error.message?.includes('Invalid email or password')) {
        userMessage = "Invalid email or password. Please check your credentials.";
      } else if (error.message) {
        userMessage = error.message;
      }
      
      setErrorMessage(userMessage);
      setRetryCount(prev => prev + 1);
      
      toast({
        variant: "destructive",
        title: "Login failed",
        description: userMessage
      });
      
      setIsSubmitting(false);
    }
  };

  // Clear session and retry login (for troubleshooting)
  const handleClearAndRetry = () => {
    console.log("Login: Clearing session and retrying");
    localStorage.removeItem('session_token');
    localStorage.removeItem('auth_user');
    setErrorMessage("");
    setRetryCount(0);
    toast({
      title: "Session cleared",
      description: "Please try logging in again"
    });
  };

  // Test database connection
  const testDatabaseConnection = async () => {
    try {
      console.log("Testing database connection...");
      const { data, error } = await supabase.from('team_members').select('count').limit(1);
      
      if (error) {
        console.error("Database test failed:", error);
        toast({
          variant: "destructive",
          title: "Database Connection Failed",
          description: `Error: ${error.message}`
        });
      } else {
        console.log("Database connection successful");
        toast({
          title: "Database Connection Successful",
          description: "The database is accessible. The issue may be with authentication functions."
        });
      }
    } catch (error) {
      console.error("Connection test error:", error);
      toast({
        variant: "destructive",
        title: "Connection Test Failed",
        description: "Unable to test database connection"
      });
    }
  };

  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const shouldShowLoginForm = isLoading || !isAuthenticated;
  
  if (!shouldShowLoginForm) {
    console.log("Login: User already authenticated, waiting for redirect");
    return (
      <div className="min-h-screen flex items-center justify-center bg-cs-purple">
        <div className="neo-blur p-8 rounded-lg shadow text-center">
          <div className="animate-spin h-6 w-6 border-4 border-white border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-white">Already authenticated. Redirecting...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden bg-cs-purple flex flex-col items-center justify-center p-4">
      {/* Flowing Waves Background */}
      <div className="wave-container">
        <div className="wave wave1"></div>
        <div className="wave wave2"></div>
        <div className="wave wave3"></div>
        <div className="wave wave4"></div>
      </div>
      
      <div className="w-full max-w-md space-y-8 relative z-10 animate-fade-in">
        <div className="flex flex-col items-center justify-center space-y-6 text-center mb-8">
          <div className="ultra-glass-banner rounded-2xl p-8 mb-4 shadow-2xl backdrop-blur-2xl border border-white/10 transition-all duration-500">
            <div className="flex flex-col items-center space-y-4">
              <div className="bg-white/10 backdrop-blur-lg rounded-full p-4 shadow-xl border border-white/20">
                <Logo className="text-white" textColor="text-white" />
              </div>
            </div>
          </div>
          <h2 className="text-2xl font-semibold text-white/95">PROJECTS</h2>
        </div>
        
        <div className="neo-blur p-10 rounded-3xl shadow-2xl backdrop-blur-2xl border border-white/20 transition-all duration-500 hover:shadow-3xl hover:border-white/30">
          <form onSubmit={handleSubmit} className="space-y-8">
            {errorMessage && (
              <div className="bg-red-500/20 backdrop-blur-sm border border-red-300/30 text-white px-5 py-4 rounded-xl text-sm animate-fade-in font-medium">
                {errorMessage}
                {retryCount >= 2 && (
                  <div className="mt-3 pt-3 border-t border-red-300/30">
                    <p className="text-xs text-white/80 mb-2">
                      Having trouble? Try these troubleshooting steps:
                    </p>
                    <div className="space-y-2">
                      <button
                        type="button"
                        onClick={handleClearAndRetry}
                        className="flex items-center gap-2 text-xs bg-red-400/20 hover:bg-red-400/30 px-3 py-1 rounded-lg transition-colors mr-2"
                      >
                        <RefreshCw className="h-3 w-3" />
                        Clear Session
                      </button>
                      <button
                        type="button"
                        onClick={testDatabaseConnection}
                        className="flex items-center gap-2 text-xs bg-blue-400/20 hover:bg-blue-400/30 px-3 py-1 rounded-lg transition-colors"
                      >
                        Test Database
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            <div className="space-y-3 group">
              <Label htmlFor="email" className="brutalist text-white/90 group-hover:text-white transition-all text-sm font-semibold uppercase tracking-wider">
                Email Address
              </Label>
              <div className="relative">
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="glassmorphic-input h-14 text-white placeholder:text-white/60 text-lg rounded-xl"
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>
            
            <div className="space-y-3 group">
              <Label htmlFor="password" className="brutalist text-white/90 group-hover:text-white transition-all text-sm font-semibold uppercase tracking-wider">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="glassmorphic-input h-14 text-white placeholder:text-white/60 text-lg rounded-xl pr-12"
                  required
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 flex items-center px-4 text-white/70 hover:text-white transition-all"
                  onClick={togglePasswordVisibility}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            
            <Button
              type="submit"
              className="w-full h-14 brutalist-button text-white text-lg font-bold tracking-wide rounded-xl relative overflow-hidden group"
              disabled={isSubmitting}
            >
              <span className="relative z-10">
                {isSubmitting ? "Signing in..." : "SIGN IN"}
              </span>
              <div className="absolute inset-0 bg-white/0 group-hover:bg-white/10 transform transition-all duration-500 scale-x-0 group-hover:scale-x-100 origin-left"></div>
            </Button>
          </form>
          
          <div className="mt-8">
            <div className="text-center text-sm">
              <a
                onClick={() => toast({
                  title: "Password Reset",
                  description: "Please contact your administrator to reset your password."
                })}
                className="text-white/80 hover:text-white cursor-pointer transition-all hover:underline font-medium"
              >
                Forgot password?
              </a>
            </div>
            
            {retryCount >= 3 && (
              <div className="mt-4 p-3 bg-white/10 rounded-lg">
                <p className="text-xs text-white/80 text-center">
                  Need help? Contact your system administrator if login issues persist.
                  The authentication system may need to be set up or repaired.
                </p>
              </div>
            )}
          </div>
        </div>
        
        <div className="text-center text-sm text-white/60">
          <p>© 2025 City & Sanctuary. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

// Login error fallback component
const LoginErrorFallback = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-cs-purple to-cs-purple-dark flex items-center justify-center p-4">
      <div className="neo-blur backdrop-blur-xl bg-white/10 border border-white/20 p-8 rounded-xl shadow-lg max-w-md w-full">
        <h2 className="text-xl font-bold mb-4 text-white">City & Sanctuary</h2>
        <p className="mb-4 text-white/90">We encountered an error while loading the login page. Please try again.</p>
        <div className="space-y-3">
          <Button onClick={() => window.location.reload()} className="w-full bg-white/20 text-white hover:bg-white/30 brutalist-button transition-all">
            Refresh Page
          </Button>
          <Button 
            onClick={() => {
              localStorage.removeItem('session_token');
              localStorage.removeItem('auth_user');
              window.location.reload();
            }} 
            className="w-full bg-white/10 text-white hover:bg-white/20 brutalist-button transition-all"
          >
            Clear Session & Refresh
          </Button>
        </div>
      </div>
    </div>
  );
};

// Wrap the entire Login component in an error boundary
const Login = () => {
  console.log("Login page is being rendered");

  useEffect(() => {
    clearRedirectionFlags();
    console.log("Login (outer): Cleared redirection flags");
  }, []);

  return (
    <ErrorBoundary fallback={<LoginErrorFallback />}>
      <LoginContent />
    </ErrorBoundary>
  );
};

export default Login;
